import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  addCalendar,
  deleteCalendar,
  getCalendars,
  syncCalendar,
  moveCalendarUp,
  moveCalendarDown,
  ApartmentCalendar,
  CalendarSource,
  CalendarSyncResult,
} from '../api.calendar';
import { useLang } from '../i18n/LanguageContext';
import { calendarManagerText } from '../i18n/pages/calendarManager';
import { channelManagerText } from '../i18n/pages/channelManager';
import ChannelManagerSection from './ChannelManagerSection';

const SYNC_COOLDOWN_MS = 60_000;

type SourceSegment = 'channel' | 'ical';

interface CalendarManagerProps {
  apartmentId: number;
}

const CalendarManager: React.FC<CalendarManagerProps> = ({ apartmentId }) => {
  const { lang } = useLang();
  const t = calendarManagerText[lang];
  const ct = channelManagerText[lang];
  // Prefer iCal for new apartments; existing API integrations must keep their compatible view.
  const [activeSegment, setActiveSegment] = useState<SourceSegment>('ical');
  const segmentDecidedRef = useRef(false);
  const calendarSourceOptions: Array<{ value: CalendarSource; label: string }> = [
    { value: 'AIRBNB', label: 'Airbnb' },
    { value: 'BOOKING', label: 'Booking' },
    { value: 'HOSTEX', label: 'Hostex' },
    { value: 'HOSPITABLE', label: 'Hospitable' },
    { value: 'ECHALUPY', label: 'e-Chalupy' },
    { value: 'OTHER', label: 'Other' },
  ];
  const [calendars, setCalendars] = useState<ApartmentCalendar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  // calendarId → timestamp when sync becomes available again
  const [syncCooldowns, setSyncCooldowns] = useState<Record<number, number>>({});
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const anyActive = Object.values(syncCooldowns).some(t => t > Date.now());
    if (!anyActive) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    if (tickRef.current) return;
    tickRef.current = setInterval(() => {
      setSyncCooldowns(prev => {
        const next = { ...prev };
        let changed = false;
        for (const [id, t] of Object.entries(next)) {
          if (t <= Date.now()) { delete next[Number(id)]; changed = true; }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };
  }, [syncCooldowns]);
  const [syncResult, setSyncResult] = useState<CalendarSyncResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [infoOpen, setInfoOpen] = useState<boolean>(
    () => localStorage.getItem('calendar_sync_info_expanded') === 'true'
  );
  const [newCalendar, setNewCalendar] = useState<{ name: string; url: string; source: CalendarSource }>({
    name: '',
    url: '',
    source: 'AIRBNB',
  });

  const detectSourceFromUrl = (url: string): CalendarSource | null => {
    const lower = url.toLowerCase();
    if (lower.includes('airbnb')) return 'AIRBNB';
    if (lower.includes('booking')) return 'BOOKING';
    if (lower.includes('hostex')) return 'HOSTEX';
    if (lower.includes('hospitable')) return 'HOSPITABLE';
    if (lower.includes('e-chalupy.cz')) return 'ECHALUPY';
    return null;
  };

  // Returns the fetched list so ChannelManagerSection can reuse this same call instead of
  // fetching its own copy of the apartment's calendars.
  const fetchCalendars = useCallback(async (): Promise<ApartmentCalendar[]> => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCalendars(apartmentId);
      setCalendars(data);
      // Land on whichever kind of source this apartment actually uses. Only decided on the first
      // load, so a manager who deliberately switches segments is not pulled back.
      if (!segmentDecidedRef.current) {
        segmentDecidedRef.current = true;
        if (data.some(c => c.connectionType === 'PMS_API')) setActiveSegment('channel');
      }
      return data;
    } catch (err: any) {
      console.error('Failed to fetch calendars:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load calendars');
      return [];
    } finally {
      setLoading(false);
    }
  }, [apartmentId]);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  const handleAddCalendar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSyncResult(null);
    const trimmedUrl = newCalendar.url.trim();
    // API-backed sources have no URL, so they can never be the duplicate we are guarding against.
    if (calendars.some(c => c.url?.trim() === trimmedUrl)) {
      setError(t.errorDuplicate);
      return;
    }
    // Auto-detect source from URL on save (catches cases where user left it as Other)
    const detectedSource = detectSourceFromUrl(trimmedUrl);
    const finalSource = detectedSource ?? newCalendar.source;
    if (detectedSource && detectedSource !== newCalendar.source) {
      setNewCalendar(prev => ({ ...prev, source: detectedSource }));
    }
    setIsSubmitting(true);
    try {
      const result = await addCalendar(apartmentId, { ...newCalendar, url: trimmedUrl, source: finalSource });
      console.log('Calendar added successfully:', result);
      setSuccess(t.successAdded);
      setNewCalendar({ name: '', url: '', source: 'AIRBNB' });
      setShowForm(false);
      await fetchCalendars();
    } catch (err: any) {
      console.error('Failed to add calendar:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to add calendar';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCalendar = async (calendarId: number) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await deleteCalendar(apartmentId, calendarId);
      setSuccess(t.successDeleted);
      fetchCalendars();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete calendar');
    }
  };

  const handleMoveUp = async (calendarId: number) => {
    setError(null);
    try {
      const updated = await moveCalendarUp(apartmentId, calendarId);
      setCalendars(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reorder calendar');
    }
  };

  const handleMoveDown = async (calendarId: number) => {
    setError(null);
    try {
      const updated = await moveCalendarDown(apartmentId, calendarId);
      setCalendars(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reorder calendar');
    }
  };

  const handleSyncCalendar = async (calendarId: number) => {
    setError(null);
    setSuccess(null);
    setSyncResult(null);
    try {
      const result = await syncCalendar(apartmentId, calendarId);
      setSyncResult(result);
      fetchCalendars();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to sync calendar');
    } finally {
      setSyncCooldowns(prev => ({ ...prev, [calendarId]: Date.now() + SYNC_COOLDOWN_MS }));
    }
  };

  // An apartment draws from either a live channel-manager API or iCal feeds, never both. When an
  // API source exists the iCal segment is shown but disabled, so the exclusivity reads as a rule
  // rather than as a missing feature.
  const hasApiSource = calendars.some(c => c.connectionType === 'PMS_API');
  const icalCalendars = calendars.filter(c => c.connectionType !== 'PMS_API');

  return (
    <div>
      <div style={{
        display: 'inline-flex', gap: 6, background: '#f7fafc', border: '1px solid #e2e8f0',
        borderRadius: 12, padding: 6, marginBottom: 18, flexWrap: 'wrap',
      }}>
        <button
          type="button"
          onClick={() => { if (!hasApiSource) setActiveSegment('ical'); }}
          disabled={hasApiSource}
          title={hasApiSource ? ct.segIcalDisabled : undefined}
          style={{
            fontSize: '0.85rem', fontWeight: 600, padding: '7px 16px', borderRadius: 8,
            border: 'none', cursor: hasApiSource ? 'not-allowed' : 'pointer',
            background: activeSegment === 'ical' ? '#2563eb' : 'transparent',
            color: activeSegment === 'ical' ? '#fff' : hasApiSource ? '#cbd5e0' : '#718096',
          }}
        >
          {hasApiSource ? ct.segIcalDisabled : ct.segIcalFeeds}
        </button>
        <button
          type="button"
          onClick={() => setActiveSegment('channel')}
          style={{
            fontSize: '0.85rem', fontWeight: 600, padding: '7px 16px', borderRadius: 8,
            border: 'none', cursor: 'pointer',
            background: activeSegment === 'channel' ? '#2563eb' : 'transparent',
            color: activeSegment === 'channel' ? '#fff' : '#718096',
          }}
        >
          {ct.segChannelManager}
        </button>
      </div>

      {activeSegment === 'channel' && (
        <ChannelManagerSection apartmentId={apartmentId} calendars={calendars} onSourcesChanged={fetchCalendars} />
      )}

      {activeSegment === 'ical' && (
    <div>
      {/* Infographic: how sync works + overbooking */}
      <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        {/* Collapsible header */}
        <button
          onClick={() => {
            const next = !infoOpen;
            setInfoOpen(next);
            localStorage.setItem('calendar_sync_info_expanded', String(next));
          }}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        >
          <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>🔄</span> {t.title}
          </span>
          <span style={{ fontSize: '0.9rem', color: '#4a7cbf', flexShrink: 0, marginLeft: 8 }}>{infoOpen ? '▲' : '▼'}</span>
        </button>
        {infoOpen && (
        <div style={{ padding: '0 18px 16px' }}>
        {/* 4-step flow — wraps on mobile */}
        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 12, marginBottom: 14 }}>
          {/* Step 1: Platforms */}
          <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', border: '2px solid #93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>🌐</div>
            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e3a8a', marginBottom: 4 }}>{t.infoPlatformsLabel}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 3 }}>
              <span style={{ padding: '1px 5px', borderRadius: 20, fontSize: '0.6rem', fontWeight: 700, border: '1px solid #eca898', background: '#fdeae7', color: '#7f1d1d', lineHeight: 1.7 }}>Airbnb</span>
              <span style={{ padding: '1px 5px', borderRadius: 20, fontSize: '0.6rem', fontWeight: 700, border: '1px solid #93c5fd', background: '#dbeafe', color: '#1e3a8a', lineHeight: 1.7 }}>Booking.com</span>
              <span style={{ padding: '1px 5px', borderRadius: 20, fontSize: '0.6rem', fontWeight: 700, border: '1px solid #fcd34d', background: '#fef3c7', color: '#78350f', lineHeight: 1.7 }}>VRBO</span>
              <span style={{ padding: '1px 5px', borderRadius: 20, fontSize: '0.6rem', fontWeight: 700, border: '1px solid #c4b5fd', background: '#ede9fe', color: '#4c1d95', lineHeight: 1.7 }}>{t.infoOtherPlatforms}</span>
            </div>
          </div>
          {/* Arrow */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#93c5fd', fontSize: '1.2rem', flexShrink: 0, paddingTop: 12 }}>›</div>
          {/* Step 2: iCal URL */}
          <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', border: '2px solid #93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>🔗</div>
            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e3a8a', marginBottom: 4 }}>iCal Feed URL</div>
            <div style={{ fontSize: '0.75rem', color: '#3b82f6', lineHeight: 1.4 }}>{t.infoIcalSub}</div>
          </div>
          {/* Arrow */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#93c5fd', fontSize: '1.2rem', flexShrink: 0, paddingTop: 12 }}>›</div>
          {/* Step 3: Guestbook imports */}
          <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', border: '2px solid #93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>⚙️</div>
            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e3a8a', marginBottom: 4 }}>{t.infoGuestbookLabel}</div>
            <div style={{ fontSize: '0.75rem', color: '#3b82f6', lineHeight: 1.4 }}>{t.infoGuestbookSub}</div>
          </div>
          {/* Arrow */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#93c5fd', fontSize: '1.2rem', flexShrink: 0, paddingTop: 12 }}>›</div>
          {/* Step 4: Done */}
          <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dcfce7', border: '2px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#166534', marginBottom: 4 }}>{t.infoImportedLabel}</div>
            <div style={{ fontSize: '0.75rem', color: '#16a34a', lineHeight: 1.4 }}>{t.infoImportedSub}</div>
          </div>
        </div>
        {/* Overbooking note + guide link */}
        <div style={{ borderTop: '1px solid #bfdbfe', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: '0.78rem', color: '#1d4ed8', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0 }}>⚖️</span>
            <span><strong>{t.infoPriorityTitle}</strong> {t.infoPriorityDesc}</span>
          </div>
          <a href={t.infoGuideUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.78rem', color: '#2563eb', fontWeight: 700, textDecoration: 'none' }}>
            {t.infoGuideLink}
          </a>
        </div>
        </div>
        )}
      </div>

      {syncResult && (
        <div style={{ background: '#f0fff4', border: '1px solid #68d391', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: '0.88rem', color: '#22543d' }}>
          <strong>{t.syncComplete}</strong>
          {syncResult.imported > 0
            ? <span> — {t.syncNewCount(syncResult.imported)}</span>
            : <span> — {t.syncNoNew}</span>
          }
          {syncResult.updated > 0 && <span>, {t.syncUpdatedCount(syncResult.updated)}</span>}
          {syncResult.cancelled > 0 && <span>, {t.syncCancelledCount(syncResult.cancelled)}</span>}
          {syncResult.skippedPast > 0 && (
            <span style={{ color: '#4a5568' }}> · {t.syncSkippedPastCount(syncResult.skippedPast)}</span>
          )}
        </div>
      )}
      {success && (
        <div style={{ color: 'white', padding: 16, background: '#48bb78', borderRadius: 6, marginBottom: 16, fontWeight: 600, fontSize: '1rem' }}>
          ✅ {success}
        </div>
      )}
      {error && (
        <div style={{ color: 'white', padding: 16, background: '#e53e3e', borderRadius: 6, marginBottom: 16, fontWeight: 600, fontSize: '1rem' }}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div>{t.loadingCalendars}</div>
      ) : icalCalendars.length === 0 ? (
        <div style={{ color: '#718096', marginBottom: 16 }}>{t.noCalendars}</div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          {icalCalendars.map((cal, idx) => {
            const unlockAt = syncCooldowns[cal.id];
            const coolingDown = !!unlockAt && unlockAt > Date.now();
            const remaining = coolingDown ? Math.ceil((unlockAt - Date.now()) / 1000) : 0;
            const isFirst = idx === 0;
            const isLast = idx === icalCalendars.length - 1;
            return (
              <div
                key={cal.id}
                style={{
                  padding: 12,
                  background: '#fff',
                  borderRadius: 6,
                  marginBottom: 8,
                  border: isFirst ? '1px solid #c6f6d5' : '1px solid #e2e8f0',
                }}
              >
                {/* Info row: priority controls + text */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {/* Priority controls */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0, alignItems: 'center', paddingTop: 2 }}>
                    <button
                      onClick={() => handleMoveUp(cal.id)}
                      disabled={isFirst}
                      title={t.priorityUp}
                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 3, cursor: isFirst ? 'not-allowed' : 'pointer', padding: '1px 6px', fontSize: '0.75rem', color: isFirst ? '#cbd5e0' : '#4a5568', lineHeight: 1.4 }}
                    >▲</button>
                    <span style={{ fontSize: '0.7rem', color: isFirst ? '#38a169' : '#a0aec0', fontWeight: 700, minWidth: 16, textAlign: 'center' }}>
                      {isFirst ? '★' : `${idx + 1}`}
                    </span>
                    <button
                      onClick={() => handleMoveDown(cal.id)}
                      disabled={isLast}
                      title={t.priorityDown}
                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 3, cursor: isLast ? 'not-allowed' : 'pointer', padding: '1px 6px', fontSize: '0.75rem', color: isLast ? '#cbd5e0' : '#4a5568', lineHeight: 1.4 }}
                    >▼</button>
                  </div>
                  {/* Info */}
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{cal.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#2b6cb0', fontWeight: 600, marginBottom: 6 }}>
                      {calendarSourceOptions.find((o) => o.value === cal.source)?.label || cal.source}
                    </div>
                    {/* URL row: truncated + copy button */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span
                        title={cal.url ?? undefined}
                        style={{
                          fontSize: '0.8rem',
                          color: '#718096',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {cal.url}
                      </span>
                      <button
                        onClick={() => navigator.clipboard.writeText(cal.url ?? '')}
                        title={t.copyUrl}
                        style={{
                          flexShrink: 0,
                          background: 'none',
                          border: '1px solid #e2e8f0',
                          borderRadius: 4,
                          cursor: 'pointer',
                          padding: '2px 6px',
                          fontSize: '0.75rem',
                          color: '#718096',
                          lineHeight: 1.4,
                        }}
                      >
                        {t.copyUrl}
                      </button>
                    </div>
                    {cal.lastSyncedAt && (
                      <div style={{ fontSize: '0.78rem', color: '#4a5568' }}>
                        {t.syncedAt} {new Date(cal.lastSyncedAt).toLocaleString()}
                      </div>
                    )}
                    {cal.lastSyncError && (
                      <div style={{ fontSize: '0.78rem', color: '#c53030', marginTop: 4, wordBreak: 'break-word' }}>
                        ⚠ {cal.lastSyncError}
                      </div>
                    )}
                  </div>
                </div>
                {/* Action buttons row — always on its own line */}
                <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => handleSyncCalendar(cal.id)}
                    disabled={coolingDown}
                    style={{
                      padding: '5px 12px',
                      background: coolingDown ? '#a0aec0' : '#3182ce',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: coolingDown ? 'not-allowed' : 'pointer',
                      fontSize: '0.82rem',
                      minWidth: 72,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {coolingDown ? `${remaining}s` : t.syncNow}
                  </button>
                  <button
                    onClick={() => handleDeleteCalendar(cal.id)}
                    style={{
                      padding: '5px 10px',
                      background: '#fff5f5',
                      color: '#c53030',
                      border: '1px solid #fed7d7',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                    }}
                  >
                    {t.delete}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '8px 16px',
            background: '#38a169',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          {t.addCalendar}
        </button>
      ) : (
        <form onSubmit={handleAddCalendar} style={{ padding: 16, background: '#fff', borderRadius: 8 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
              {t.formNameLabel}
            </label>
            <input
              type="text"
              value={newCalendar.name}
              onChange={(e) => setNewCalendar({ ...newCalendar, name: e.target.value })}
              required
              placeholder="Airbnb Calendar"
              style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #cbd5e0' }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
              {t.formSourceLabel}
            </label>
            <select
              value={newCalendar.source}
              onChange={(e) => setNewCalendar({ ...newCalendar, source: e.target.value as CalendarSource })}
              style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #cbd5e0' }}
            >
              {calendarSourceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
              {t.formUrlLabel}
            </label>
            <input
              type="url"
              value={newCalendar.url}
              onChange={(e) => {
                const url = e.target.value;
                const detected = detectSourceFromUrl(url);
                setNewCalendar({ ...newCalendar, url, ...(detected ? { source: detected } : {}) });
              }}
              required
              placeholder="https://www.airbnb.com/calendar/ical/..."
              style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #cbd5e0' }}
            />
            <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>
              {t.formUrlHint}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '8px 16px',
                background: isSubmitting ? '#9ae6b4' : '#38a169',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? t.adding : t.addCalendar}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setNewCalendar({ name: '', url: '', source: 'AIRBNB' });
              }}
              style={{
                padding: '8px 16px',
                background: '#e2e8f0',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {t.cancel}
            </button>
          </div>
        </form>
      )}
    </div>
      )}
    </div>
  );
};

export default CalendarManager;

